/**
 * Scout v0 — suppression paste parser (P20, client-side).
 *
 * Mirrors the backend parseBulkPaste: split on newlines + commas, lowercase +
 * trim, dedupe case-variant + whitespace-variant duplicates. The UI runs this
 * on the textarea input before POSTing, so the operator sees the normalized +
 * deduped preview before the round-trip. The backend re-normalizes (same
 * function) so client and server agree.
 *
 * Also validates each entry: a suppression value is a domain or an email. We
 * accept anything with at least one dot and no spaces after trimming — the
 * paid verification provider (Anymail) is the arbiter of real deliverability
 * (per the bouinv layer spec), so the UI gate is intentionally loose: catch
 * obvious garbage, do not over-validate.
 */

export interface ParsedPasteResult {
  /** Normalized (lowercase, trimmed), deduped values in paste order. */
  values: string[];
  /** Values rejected by the loose validity check, with a reason. */
  invalid: { raw: string; reason: string }[];
  /** Count of duplicates dropped (case/whitespace variants of an earlier entry). */
  duplicatesDropped: number;
}

/** Loose validity: has a dot, no spaces, no control chars. Not a deliverability check. */
function isLooselyValid(value: string): boolean {
  if (!value) return false;
  if (/\s/.test(value)) return false;
  if (!value.includes('.')) return false;
  // Must look like a domain or email: word chars, dots, hyphens, @, plus.
  return /^[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(value) || /^[a-z0-9.-]+\.[a-z]{2,}$/.test(value);
}

/**
 * Parse a raw paste into normalized + deduped values, splitting on newlines
 * and commas. Returns the valid values, the invalid ones (with reasons), and
 * the duplicate count.
 */
export function parseSuppressionPaste(raw: string): ParsedPasteResult {
  const seen = new Set<string>();
  const values: string[] = [];
  const invalid: { raw: string; reason: string }[] = [];
  let duplicatesDropped = 0;

  for (const piece of raw.split(/[\n,]+/)) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    const normalized = trimmed.toLowerCase();
    if (!isLooselyValid(normalized)) {
      invalid.push({ raw: trimmed, reason: 'not a domain or email' });
      continue;
    }
    if (seen.has(normalized)) {
      duplicatesDropped++;
      continue;
    }
    seen.add(normalized);
    values.push(normalized);
  }

  return { values, invalid, duplicatesDropped };
}